import React, { createContext, useContext, useState, useRef, useMemo } from 'react';
import type { FC, ReactNode } from 'react';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { MediaSharedClassNames } from '@atlaskit/editor-common/styles';

import type { CommentsDataMap } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import {
	getNextMarkerRef,
	getNextUnreadThreadKey,
	scrollToComment,
	calculateElementOffset,
} from '@confluence/comments-util/entry-points/domUtils.ts';
import {
	handleInlineHeaderArrowsNavigation,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { checkElementExpandInEditor } from '@confluence/comments-util/entry-points/editorAnnotations.ts';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	selectInlineComment,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { fg } from '@confluence/feature-gating';

import type { CommentHighlight } from './commentContextTypes.ts';

export type CreateInlineCommentHighlight = CommentHighlight;

export type HighlightOrigin = 'userHighlight' | 'nudgeHighlight';

type NavigateToInlineCommentProps = {
	action: 'next' | 'previous';
	triggeredByKeyboardShortcut: boolean;
	currentCommentIndex: number;
	onNavigationClick: (nextMarkerRef: string) => void;
	isEditor?: boolean;
	removeCommentQueryParams: () => void;
	unresolvedInlineComments: string[];
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	contentId?: string;
	isNavigateToNextUnread?: boolean;
	unreadInlineComments?: string[];
	unreadGeneralComments?: string[];
	getCommentIndex: (commentId: string) => number;
	footerComments?: string[];
	scrollToFooterComment?: (threadKey: string) => void;
	commentsDataMap?: CommentsDataMap;
};

export type ResolvedComment = {
	commentId: string;
	inlineMarkerRef: string;
};

type InlineCommentsContextValue = {
	newCommentHighlight?: CreateInlineCommentHighlight;
	highlightOrigin: HighlightOrigin;
	activeHighlight: string | null;
	firstHighlightClicked: boolean;
	shouldDisplaySidebar: boolean;
	resolvedComment: ResolvedComment | null;
};

type InlineCommentsDispatchContextValue = {
	createComment: (newHighlight: CreateInlineCommentHighlight, origin?: HighlightOrigin) => void;
	clearCreateHighlight: () => void;
	onHighlightClick: (markerRef: string | null) => void;
	toggleSidebar: (shouldDisplay: boolean) => void;
	setResolvedComment: React.Dispatch<React.SetStateAction<ResolvedComment | null>>;
	setActiveHighlight: React.Dispatch<React.SetStateAction<string | null>>;
	navigateToInlineComment: ({
		action,
		triggeredByKeyboardShortcut,
		currentCommentIndex,
		onNavigationClick,
		getCommentIndex,
		isEditor,
		removeCommentQueryParams,
		unresolvedInlineComments,
		createAnalyticsEvent,
		contentId,
		isNavigateToNextUnread,
		unreadInlineComments,
		commentsDataMap,
	}: NavigateToInlineCommentProps) => void;
};

export const InlineCommentsDispatchContext = createContext<InlineCommentsDispatchContextValue>({
	createComment: () => {},
	clearCreateHighlight: () => {},
	onHighlightClick: () => {},
	toggleSidebar: () => {},
	setResolvedComment: () => {},
	setActiveHighlight: () => {},
	navigateToInlineComment: () => {},
});
InlineCommentsDispatchContext.displayName = 'InlineCommentsDispatchContext';

export const InlineCommentsContext = createContext<InlineCommentsContextValue>({
	newCommentHighlight: undefined,
	highlightOrigin: 'userHighlight',
	activeHighlight: null,
	firstHighlightClicked: false,
	shouldDisplaySidebar: false,
	resolvedComment: null,
});
InlineCommentsContext.displayName = 'InlineCommentsContext';

type InlineCommentsProviderProps = {
	children: ReactNode;
};

export const InlineCommentsProvider: FC<InlineCommentsProviderProps> = ({ children }) => {
	const [newCommentHighlight, setNewCommentHighlight] = useState<CreateInlineCommentHighlight>();

	const [highlightOrigin, setHighlightOrigin] = useState<HighlightOrigin>('userHighlight');

	/**
	 * We eventually want this value to be the official state for loading/displaying
	 * the UI for inline comments. InlineCommentSidebar and some child components have created
	 * stateful logic (active highlight, isSidebarShown, etc) that assumes
	 * the code is always loaded which we will eventually adjust.
	 */
	const [shouldDisplaySidebar, setShouldDisplaySidebar] = useState<boolean>(false);
	const firstHighlightClicked = useRef(false);
	const [activeHighlight, setActiveHighlight] = useState<string | null>(null);
	const [resolvedComment, setResolvedComment] = useState<ResolvedComment | null>(null);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const providerValue = useMemo(
		() => ({
			newCommentHighlight,
			highlightOrigin,
			activeHighlight,
			resolvedComment,
			firstHighlightClicked: firstHighlightClicked.current,
			shouldDisplaySidebar,
		}),
		[
			activeHighlight,
			highlightOrigin,
			newCommentHighlight,
			resolvedComment,
			shouldDisplaySidebar,
			firstHighlightClicked,
		],
	);

	const dispatchProviderValue = useMemo(
		() => ({
			createComment: (
				newHighlight: CreateInlineCommentHighlight,
				origin: HighlightOrigin = 'userHighlight',
			) => {
				/**
				 * Creating a comment is one of the three user explicit scenarios
				 * where we want to load inline comments code.
				 */
				setShouldDisplaySidebar(true);
				setNewCommentHighlight(newHighlight);
				setHighlightOrigin(origin);
				if (resolvedComment) {
					setResolvedComment(null);
				}
			},
			clearCreateHighlight: () => setNewCommentHighlight(undefined),
			onHighlightClick: (markerRef: string | null) => {
				if (!firstHighlightClicked.current) {
					firstHighlightClicked.current = true;
				}

				/**
				 * Clicking on a highlight is one of the three user explicit scenarios
				 * where we want to load inline comments code.
				 */
				if (markerRef) {
					setShouldDisplaySidebar(true);
				} else {
					setShouldDisplaySidebar(false);
				}

				setActiveHighlight(markerRef);
			},
			setResolvedComment,
			toggleSidebar: (shouldDisplay: boolean) => setShouldDisplaySidebar(shouldDisplay),
			setActiveHighlight,
			navigateToInlineComment: ({
				action,
				triggeredByKeyboardShortcut,
				currentCommentIndex,
				onNavigationClick,
				isEditor,
				removeCommentQueryParams,
				unresolvedInlineComments,
				createAnalyticsEvent,
				contentId,
				isNavigateToNextUnread,
				unreadInlineComments = [],
				unreadGeneralComments = [],
				getCommentIndex,
				footerComments = [],
				scrollToFooterComment,
				commentsDataMap,
			}: NavigateToInlineCommentProps) => {
				selectInlineCommentCompoundExperience.addCommonAttributes({ navigationAction: action });

				const couldBeNavigatingToFooterComment =
					triggeredByKeyboardShortcut &&
					footerComments?.length > 0 &&
					fg('confluence_frontend_content_wrapper');

				if (unresolvedInlineComments?.length < 2 && !couldBeNavigatingToFooterComment) {
					selectInlineCommentCompoundExperience.selectAnnotation.abort({
						abortReason: 'Comment navigation when only one comment',
					});

					if (fg('confluence_frontend_view_inline_comment_exp_parity')) {
						experienceTracker.abort({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							reason: 'Comment navigation when only one comment',
							checkForTimeout: true,
							publishDebugPoints: true,
							attributes: {
								stopPoint: 'comment-context/InlineCommentsContext',
							},
						});
					}

					return;
				}

				// Clear the focusedCommentId queryParams so that reply does not opens up if any...
				removeCommentQueryParams();

				const combinedInlineFooterComments = [...unresolvedInlineComments, ...footerComments];
				let nextThreadKey = '';
				let currentThreadKey = '';

				if (triggeredByKeyboardShortcut && fg('confluence_frontend_content_wrapper')) {
					if (isNavigateToNextUnread) {
						if (fg('confluence_frontend_unread_comments_fixes')) {
							// Add 1 to currentCommentIndex to move to the next comment, otherwise it gets stuck on the current comment
							const nextIndex = currentCommentIndex + 1;
							nextThreadKey =
								getNextUnreadThreadKey(nextIndex, combinedInlineFooterComments, [
									...unreadInlineComments,
									...unreadGeneralComments,
								]) ?? '';
						} else {
							// Legacy behavior when feature flag is disabled
							nextThreadKey =
								getNextUnreadThreadKey(currentCommentIndex, combinedInlineFooterComments, [
									...unreadInlineComments,
									...unreadGeneralComments,
								]) ?? '';
						}
					} else {
						nextThreadKey =
							getNextMarkerRef(action, currentCommentIndex, combinedInlineFooterComments) ?? '';
					}
					currentThreadKey = combinedInlineFooterComments[currentCommentIndex];
				} else {
					nextThreadKey =
						getNextMarkerRef(action, currentCommentIndex, unresolvedInlineComments) ?? '';
					currentThreadKey = unresolvedInlineComments[currentCommentIndex];
				}

				if (!nextThreadKey) {
					selectInlineComment.debug(`Couldn't find next annotation to select in document`);
					return;
				}

				selectInlineCommentCompoundExperience.addCommonAttributes({
					annotationId: nextThreadKey,
				});

				if (triggeredByKeyboardShortcut && fg('confluence_frontend_content_wrapper')) {
					if (
						footerComments.includes(nextThreadKey) &&
						scrollToFooterComment &&
						commentsDataMap?.general?.[nextThreadKey]
					) {
						setActiveHighlight(null);
						scrollToFooterComment(nextThreadKey);

						selectInlineCommentCompoundExperience.selectAnnotation.abort({
							abortReason: 'Navigating to general comment',
						});

						return;
					}
				}

				const nextCommentElement =
					document.getElementById(nextThreadKey) ||
					(fg('confluence_frontend_content_wrapper')
						? document.getElementById(`footer-comment-thread-${nextThreadKey}-container`)
						: null);
				const previousCommentElement =
					document.getElementById(currentThreadKey) ||
					(fg('confluence_frontend_content_wrapper')
						? document.getElementById(`footer-comment-thread-${currentThreadKey}-container`)
						: null);

				if (nextCommentElement) {
					checkElementExpandInEditor(nextCommentElement);
				} else {
					selectInlineComment.debug('No annotation found in doc to check if in expand');
				}

				onNavigationClick(nextThreadKey);

				const nextCommentIndex = getCommentIndex(nextThreadKey);

				let heightOfMediaCommentSelectedPlaceholder = 0;

				if (fg('confluence_frontend_new_comments_scrolling')) {
					const isCurrentCommentContentMediaComment =
						previousCommentElement?.closest('.media-single-node');
					const isNavigatingToLastCommentFromPreviousAction =
						action === 'previous' && nextCommentIndex === unresolvedInlineComments.length - 1;
					const isNavigatingToFirstCommentFromNextAction =
						action === 'next' && nextCommentIndex === 0;

					if (
						isCurrentCommentContentMediaComment &&
						(action === 'next' ||
							isNavigatingToLastCommentFromPreviousAction ||
							isNavigatingToFirstCommentFromNextAction)
					) {
						// if the media comment placeholder is visible, it pushes the contents of the page down, which affects navigating to the prev and next comment
						// this means we'll need to scroll a bit less so that the cursor is still over the navigation arrow icon
						const mediaCommentSelectedPlaceholder = document.querySelector(
							'[data-id="caption-placeholder"]',
						) as HTMLElement;
						heightOfMediaCommentSelectedPlaceholder = mediaCommentSelectedPlaceholder
							? mediaCommentSelectedPlaceholder.offsetHeight
							: 0;
					}
				}

				const previousOffset = previousCommentElement
					? calculateElementOffset(previousCommentElement)
					: 0;

				const offset = calculateElementOffset(nextCommentElement);

				const scrollableContainerSelector = isEditor
					? '.fabric-editor-popup-scroll-parent'
					: '#content-body';

				const scrollableContainer = document.querySelector(scrollableContainerSelector);

				const differenceOffset = offset - previousOffset - heightOfMediaCommentSelectedPlaceholder;

				const enableOnArrowNavigation = fg('confluence_comments_disable_scroll')
					? !triggeredByKeyboardShortcut
					: true;

				// Feature gate: Use unified navigation or legacy scrolling
				if (enableOnArrowNavigation) {
					if (fg('confluence_comments_unified_navigation')) {
						// Use unified navigation
						handleInlineHeaderArrowsNavigation({
							previousMarkerRef: currentThreadKey,
							nextMarkerRef: nextThreadKey,
							context: isEditor ? NavigationContext.EDITOR : NavigationContext.RENDERER,
						});
					} else {
						// Use legacy scrolling (scrollBy + scrollToComment)
						scrollableContainer?.scrollBy(0, differenceOffset);
						scrollToComment(nextCommentElement, previousCommentElement, true, Boolean(isEditor));
					}
				}
				if (nextCommentElement) {
					// As we navigate inline comments with arrow icons, we want to update the cursor location to where the open inline comment is
					// unless it is a media node in which case, we want our selection to be the media node element.
					const media = nextCommentElement.querySelector(
						`.${MediaSharedClassNames.MEDIA_CONTAINER}`,
					);
					if (media) {
						selectInlineCommentCompoundExperience.addCommonAttributes({
							commentType: 'block',
							blockType: 'media',
						});
						enableOnArrowNavigation && window.getSelection()?.setPosition(media);
					} else {
						selectInlineCommentCompoundExperience.addCommonAttributes({ commentType: 'inline' });
						enableOnArrowNavigation && window.getSelection()?.setPosition(nextCommentElement);
					}
				}

				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'navigate',
						actionSubjectId: 'inlineCommentArrowsNavigate',
						attributes: {
							triggeredByKeyboardShortcut,
							action,
						},
						objectType: 'inlineComment',
						objectId: contentId,
						source: isEditor ? 'editPageScreen' : 'renderPageScreen',
					},
				}).fire();
			},
		}),
		[
			resolvedComment,
			firstHighlightClicked,
			setShouldDisplaySidebar,
			setHighlightOrigin,
			setResolvedComment,
			setActiveHighlight,
			experienceTracker,
		],
	);

	return (
		<InlineCommentsContext.Provider value={providerValue}>
			<InlineCommentsDispatchContext.Provider value={dispatchProviderValue}>
				{children}
			</InlineCommentsDispatchContext.Provider>
		</InlineCommentsContext.Provider>
	);
};

export const useInlineCommentsContext = () => {
	return useContext(InlineCommentsContext);
};

export const useInlineCommentsDispatchContext = () => {
	return useContext(InlineCommentsDispatchContext);
};
